import React from 'react';
import {
  ColumnPinningState,
  ExpandedState,
  OnChangeFn,
  Table,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { DATA_GRID_ROW_HEADER_COLUMN_ID } from './constants';
import {
  DataGridColumn,
  DataGridColumnGroup,
  DataGridColumnNode,
  DataGridExpansionConfig,
  DataGridRowHeaderConfig,
  DataGridRowsConfig,
} from './types';
import {
  buildDataGridColumnModel,
  buildDataGridRowCanExpand,
  toDataGridExpandedIds,
  toDataGridExpandedState,
} from './utils';

export interface UseDataGridParams<TRow> {
  data: TRow[];
  columns: DataGridColumnNode<TRow>[];
  rowHeaderConfig: DataGridRowHeaderConfig<TRow>;
  rowsConfig: DataGridRowsConfig<TRow>;
  expansionConfig?: DataGridExpansionConfig;
}

export interface UseDataGridResult<TRow> {
  table: Table<TRow>;
  leafColumnsById: Map<string, DataGridColumn<TRow>>;
  groupColumnsById: Map<string, DataGridColumnGroup<TRow>>;
}

export const useDataGrid = <TRow,>({
  data,
  columns,
  rowHeaderConfig,
  rowsConfig,
  expansionConfig,
}: UseDataGridParams<TRow>): UseDataGridResult<TRow> => {
  const isExpansionEnabled = expansionConfig?.isEnabled ?? false;

  const { columnDefs, leafColumnsById, groupColumnsById } = React.useMemo(
    () => buildDataGridColumnModel(columns, rowHeaderConfig),
    [columns, rowHeaderConfig]
  );

  const columnPinning = React.useMemo<ColumnPinningState>(
    () => ({
      left:
        rowHeaderConfig.isPinned === false
          ? []
          : [DATA_GRID_ROW_HEADER_COLUMN_ID],
      right: [],
    }),
    [rowHeaderConfig.isPinned]
  );

  const controlledExpanded = React.useMemo(
    () =>
      expansionConfig?.expandedIds
        ? toDataGridExpandedState(expansionConfig.expandedIds)
        : undefined,
    [expansionConfig?.expandedIds]
  );
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    React.useState<ExpandedState>(() =>
      toDataGridExpandedState(expansionConfig?.defaultExpandedIds)
    );
  const expanded = controlledExpanded ?? uncontrolledExpanded;

  const onGridExpandedChange: OnChangeFn<ExpandedState> = (updater) => {
    const nextExpanded =
      typeof updater === 'function' ? updater(expanded) : updater;
    if (!controlledExpanded) {
      setUncontrolledExpanded(nextExpanded);
    }
    expansionConfig?.onExpandedChange?.(toDataGridExpandedIds(nextExpanded));
  };

  const getRowCanExpand = buildDataGridRowCanExpand<TRow>(
    isExpansionEnabled,
    expansionConfig
  );

  const table = useReactTable<TRow>({
    data,
    columns: columnDefs,
    state: { columnPinning, expanded },
    getRowId: (row) => rowsConfig.getRowId(row),
    getSubRows: rowsConfig.getSubRows,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: onGridExpandedChange,
    getRowCanExpand,
    enableExpanding: isExpansionEnabled,
    enableColumnPinning: true,
  });

  return { table, leafColumnsById, groupColumnsById };
};
